import dayjs from "dayjs";

import {
  LINE_ORIGINS,
  REVIEW_STATES,
  TERMINAL_REVIEW_STATES,
  entityTypeForLine,
  isCurrencyUnit,
  requiresEntity,
  requiresTaxonomy,
  requiresVendor
} from "../../../helpers/smartUploadLineRequirements";

export const DOCUMENT_FAMILY_LABELS = {
  utility_bill: "Utility bill",
  fuel_receipt: "Fuel receipt",
  travel_invoice: "Travel invoice",
  freight_invoice: "Freight invoice",
  vendor_scope3_expense: "Vendor invoice",
  transactions_csv: "Transactions CSV",
  shipping_transactions: "Shipping CSV",
  bom_products_csv: "Product BOM CSV",
  utilities_csv: "Utilities CSV",
  office_csv: "Office usage CSV",
  unclassified: "Document"
};

// A background "Confirm N ready lines" run keeps review open: attention
// lines can still be resolved while it works.
export const REVIEWABLE_TASK_STATUSES = [
  "ready_for_review",
  "partially_reviewed",
  "bulk_committing"
];

const LINE_DEFAULTS = {
  quantity_final: null,
  unit_final: null,
  period_start_final: null,
  period_end_final: null,
  subcategory_final: null,
  fuel_type_final: null,
  needs_manual_categorization: false,
  emission_factor_id: null,
  taxonomy_candidates: [],
  requires_entity: false,
  requires_vendor: false,
  allocated_entity_final: null,
  allocation_match_source: null,
  allocation_candidates: [],
  allocation_confirmed: false,
  allocation_conflict: null,
  vendor_name_ai: null,
  vendor_match_ai: null,
  vendor_match_accepted: false,
  vendor_name_final: null,
  vendor_relationship_id_final: null,
  force_new_vendor: false,
  confidence_by_field: {},
  review_state: REVIEW_STATES.pendingReview,
  edited_fields: [],
  acknowledged_fields: [],
  duplicate_of: null,
  duplicate_resolution: null,
  commit_error: null,
  document_filing_error: null,
  write_attempted_at: null,
  source_page: null,
  origin: LINE_ORIGINS.extracted
};

export const isManualOriginLine = (line) =>
  line.origin === LINE_ORIGINS.manualFallback;

// setCollectionDoc strips nulls server-side, so a missing field reads as its
// default here -- the same "missing means null" rule checkCommitGates uses.
export const readLine = (line = {}) => ({
  ...LINE_DEFAULTS,
  ...Object.fromEntries(
    Object.entries(line).filter(
      ([, value]) => value !== null && value !== undefined
    )
  )
});

// Recomputes the requirement flags from a draft's own values, so an edited
// category or unit shows its new Entity/Vendor needs before the server's
// snapshot comes back.
export const withDerivedRequirements = (line, documentFamily) => ({
  ...line,
  requires_entity: requiresEntity({
    documentFamily,
    subcategory: line.subcategory_final
  }),
  requires_vendor: requiresVendor({
    documentFamily,
    unit: line.unit_final,
    subcategory: line.subcategory_final
  })
});

export const lineKeyFor = ({ rowId, lineId }) => `${rowId}/${lineId}`;

export const isLineActionable = ({ review_state: reviewState }) =>
  reviewState === REVIEW_STATES.pendingReview ||
  reviewState === REVIEW_STATES.error;

export const isLineTerminal = ({ review_state: reviewState }) =>
  TERMINAL_REVIEW_STATES.has(reviewState);

// `is_view_only` is a client-only flag the review row sets while the batch
// can't be edited (still extracting, or finished); it is never sent. A
// manual-fallback line's values change only by re-saving through the dialog
// (server: manual_line_values_locked), so it renders read-only here too.
export const areValuesLocked = (line) =>
  !!line.is_view_only ||
  !!line.write_attempted_at ||
  !isLineActionable(line) ||
  isManualOriginLine(line);

export const UNDOABLE_REVIEW_STATES = [
  REVIEW_STATES.rejected,
  REVIEW_STATES.manualFallback,
  REVIEW_STATES.duplicateSkipped
];

// Matches commitSmartUploadLines.js's CLAIM_STALE_MS: past this, a new commit
// may take over a line left in "committing" by a crashed or timed-out call.
export const CLAIM_STALE_MS = 10 * 60 * 1000;

const toMillis = (value) =>
  value?.toMillis?.() ?? (value ? new Date(value).getTime() : 0);

export const claimAgeMs = (line, now) =>
  now - toMillis(line.committing_claimed_at);

export const isClaimStale = (line, now) =>
  claimAgeMs(line, now) > CLAIM_STALE_MS;

export const lineEntityType = (line, documentFamily) =>
  entityTypeForLine({ documentFamily, subcategory: line.subcategory_final });

export const isVendorFieldShown = (line) =>
  line.requires_vendor ||
  isCurrencyUnit(line.unit_final) ||
  !!line.vendor_relationship_id_final;

// Same resolution rule as the server's checkCommitGates: a low field clears
// when acknowledged or edited, and picking a category also clears the
// activity-type field. An allocation field only counts on a physical line.
const FIELDS_RESOLVING_CONFIDENCE = {
  quantity: ["quantity"],
  unit: ["unit"],
  period: ["period"],
  activity_type: ["activity_type", "mapping"],
  mapping: ["mapping"],
  allocation: ["allocation"]
};

const isLowFieldCleared = (
  { acknowledged_fields: acknowledged, edited_fields: edited },
  field
) =>
  (FIELDS_RESOLVING_CONFIDENCE[field] || [field]).some(
    (resolvingField) =>
      acknowledged.includes(resolvingField) || edited.includes(resolvingField)
  );

const isConfidenceFieldRelevant = (line, field) =>
  field !== "allocation" || !!line.requires_entity;

export const unclearedLowFields = (line) =>
  Object.entries(line.confidence_by_field || {})
    .filter(
      ([field, level]) =>
        level === "low" && isConfidenceFieldRelevant(line, field)
    )
    .map(([field]) => field)
    .filter((field) => !isLowFieldCleared(line, field));

export const isVendorMatchAccepted = ({
  vendor_match_ai: vendorMatch,
  vendor_match_accepted: isAccepted,
  vendor_name_final: vendorNameFinal,
  force_new_vendor: forceNewVendor
}) =>
  !!vendorMatch?.vendorId &&
  !forceNewVendor &&
  (!!isAccepted || (!!vendorMatch.isConfirmed && !vendorNameFinal));

// The Vendor field pre-fills from the issuer name; confirming a line accepts
// that pre-fill, so it is persisted as vendor_name_final right before commit
// (the server gate reads only persisted fields). Only for lines that need a
// vendor: on an activity line the issuer is usually the utility or fuel
// station, and persisting it would research and create a vendor nobody asked
// for.
export const vendorPrefillToPersist = (line) => {
  if (!line.requires_vendor) {
    return null;
  }
  const hasVendor =
    !!line.vendor_relationship_id_final ||
    !!line.vendor_name_final ||
    isVendorMatchAccepted(line);
  if (hasVendor || !line.vendor_name_ai) {
    return null;
  }
  return line.vendor_name_ai;
};

const hasResolvedVendor = (line) =>
  !!line.vendor_relationship_id_final ||
  !!line.vendor_name_final ||
  isVendorMatchAccepted(line) ||
  !!line.vendor_name_ai ||
  !!line.plaid_enrichment?.vendor?.name;

// A migrated CSV line has no taxonomy step (requiresTaxonomy), so the
// confidence, category and factor gates don't apply -- same as the server.
const needsTaxonomy = (line) =>
  requiresTaxonomy({ documentFamily: line.document_family });

export const isAllocationSatisfied = ({
  allocated_entity_final: allocatedEntity,
  allocation_match_source: matchSource,
  allocation_confirmed: isConfirmed,
  allocation_conflict: conflict
}) => {
  if (!allocatedEntity?.entity_id) {
    return false;
  }
  if (conflict) {
    return isConfirmed;
  }
  return matchSource === "context" || matchSource === "learned" || isConfirmed;
};

// Mirrors the server's checkCommitGates (spec Part B step 2); reason strings
// are the ones commit-lines returns, so one copy map covers both.
export const lineGateFailures = (line) =>
  [
    needsTaxonomy(line) &&
      !!unclearedLowFields(line).length &&
      "low_confidence_unacknowledged",
    needsTaxonomy(line) &&
      (!line.subcategory_final || line.needs_manual_categorization) &&
      "category_required",
    needsTaxonomy(line) &&
      !!line.subcategory_final &&
      !line.needs_manual_categorization &&
      !line.emission_factor_id &&
      "emission_factor_missing",
    line.requires_entity && !isAllocationSatisfied(line) && "entity_required",
    !!line.duplicate_of &&
      line.duplicate_resolution !== "keep" &&
      "duplicate_unresolved",
    line.requires_vendor && !hasResolvedVendor(line) && "vendor_required"
  ].filter(Boolean);

// Error lines count too: a retry is just another commit, and the server
// re-runs every gate.
export const isLineReady = (line) =>
  isLineActionable(line) && !lineGateFailures(line).length;

export const LINE_REASON_COPY = {
  low_confidence_unacknowledged:
    "Check the highlighted value — edit it or mark it “Looks right.”",
  category_required: "Pick a category for this line.",
  emission_factor_missing:
    "We can't calculate emissions from this unit — check the unit.",
  entity_required: "Pick where this belongs.",
  duplicate_unresolved: "Choose Skip or Keep both.",
  duplicate_detected:
    "This looks like something already added — Skip or Keep both.",
  vendor_required: "Add the vendor for this line.",
  slideout_prompt_required: "Confirm where these emissions should go first.",
  vendor_factor_missing:
    "We couldn't find an emission factor for this vendor — pick a different vendor or enter manually.",
  vendor_is_utility:
    "This looks like a utility — enter usage (kWh/therms) instead of dollars, or enter manually.",
  utility_needs_usage: "Enter the kWh or therms from this bill.",
  unsupported_calculation:
    "We can't calculate this line automatically — enter it manually.",
  fuel_type_unknown: "Choose the fuel type for this line.",
  unit_not_convertible: "We couldn't convert this unit — check the unit.",
  missing_office_grid_data:
    "This building is missing the location data we need — update the building or enter manually.",
  missing_period: "Add the period this line covers.",
  invalid_quantity: "Check the quantity.",
  committed_values_mismatch:
    "This line was already recorded with different values — correct it from the activity record.",
  invalid_source_path:
    "We couldn't read this file's source — enter it manually."
};

export const reasonCopy = (reason) =>
  LINE_REASON_COPY[reason] || "This line couldn't be recorded yet.";

const pluralLines = (count, singularVerb, pluralVerb) =>
  `${count} ${count === 1 ? "line" : "lines"} ${
    count === 1 ? singularVerb : pluralVerb
  }`;

const ENTITY_NOUN = { offices: "a building", vehicles: "a vehicle" };

const SUMMARY_COPY = {
  low_confidence_unacknowledged: (count) =>
    `${pluralLines(count, "has", "have")} a value to check`,
  category_required: (count) =>
    `${pluralLines(count, "needs", "need")} a category`,
  emission_factor_missing: (count) =>
    `${pluralLines(count, "needs", "need")} a unit we can calculate from`,
  "entity_required:offices": (count) =>
    `${pluralLines(count, "needs", "need")} ${ENTITY_NOUN.offices}`,
  "entity_required:vehicles": (count) =>
    `${pluralLines(count, "needs", "need")} ${ENTITY_NOUN.vehicles}`,
  duplicate_unresolved: (count) =>
    `${pluralLines(count, "looks", "look")} already added — Skip or Keep both`,
  vendor_required: (count) => `${pluralLines(count, "needs", "need")} a vendor`
};

const summaryKeyFor = (reason, line, documentFamily) =>
  reason === "entity_required"
    ? `${reason}:${lineEntityType(line, documentFamily)}`
    : reason;

// Named-count reasons for a disabled "Confirm all", e.g. "1 line needs a
// building · 2 lines need a category" (Design Intent 3.4: a hard block, never
// a dismissible warning).
export const summarizeGateFailures = (entries) => {
  const counts = entries
    .filter(({ line }) => isLineActionable(line))
    .flatMap(({ line, documentFamily }) =>
      lineGateFailures(line).map((reason) =>
        summaryKeyFor(reason, line, documentFamily)
      )
    )
    .reduce((tally, key) => ({ ...tally, [key]: (tally[key] || 0) + 1 }), {});

  return Object.keys(SUMMARY_COPY)
    .filter((key) => counts[key])
    .map((key) => SUMMARY_COPY[key](counts[key]));
};

const toDate = (value) => (value?.toDate ? value.toDate() : value);

export const buildClosedYears = (accountingYears = []) =>
  new Set(
    accountingYears
      .map(({ date }) => (date ? dayjs(toDate(date)).year() : null))
      .filter(Boolean)
  );

export const isInClosedYear = (line, closedYears) => {
  const effectiveDate = line.period_end_final || line.period_start_final;
  return (
    !!effectiveDate && closedYears.has(dayjs(toDate(effectiveDate)).year())
  );
};

const normalizeVendorName = (name) => (name || "").trim().toLowerCase();

// A name "needs creation" when nothing already links the line to a vendor:
// no picked relationship and no accepted vendor_match_ai. Countable without
// any LLM call, the same rule commit-lines uses for its new-vendor cap.
// Mirrors the server's cap: an optional-vendor line counts only when the
// customer typed a vendor name, since that name is what gets researched.
export const newVendorKeyFor = (line) => {
  const needsName = line.requires_vendor || !!line.vendor_name_final?.trim();
  if (!needsName || line.vendor_relationship_id_final) {
    return null;
  }
  if (isVendorMatchAccepted(line)) {
    return null;
  }
  const name = normalizeVendorName(
    line.vendor_name_final || line.vendor_name_ai
  );
  return name || null;
};

const EMPTY_CHUNK = { lines: [], vendorKeys: [] };

const fitsChunk = (chunk, vendorKey, { maxLines, maxNewVendors }) =>
  chunk.lines.length < maxLines &&
  (!vendorKey ||
    chunk.vendorKeys.includes(vendorKey) ||
    chunk.vendorKeys.length < maxNewVendors);

const addToChunk = (chunk, entry, vendorKey) => ({
  lines: [...chunk.lines, entry],
  vendorKeys:
    vendorKey && !chunk.vendorKeys.includes(vendorKey)
      ? [...chunk.vendorKeys, vendorKey]
      : chunk.vendorKeys
});

// commit-lines accepts at most 100 lines and 5 distinct vendor names needing
// creation per call; grouping by vendor keeps one issuer's lines together.
export const chunkCommitEntries = (
  entries,
  { maxLines = 100, maxNewVendors = 5 } = {}
) =>
  [...entries]
    .sort((first, second) =>
      (first.vendorKey || "").localeCompare(second.vendorKey || "")
    )
    .reduce((chunks, entry) => {
      const lastChunk = chunks[chunks.length - 1];
      if (
        lastChunk &&
        fitsChunk(lastChunk, entry.vendorKey, { maxLines, maxNewVendors })
      ) {
        return [
          ...chunks.slice(0, -1),
          addToChunk(lastChunk, entry, entry.vendorKey)
        ];
      }
      return [...chunks, addToChunk(EMPTY_CHUNK, entry, entry.vendorKey)];
    }, [])
    .map(({ lines }) => lines);
